import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Mail, Globe, Server } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

/* ═══════ SUB-PROCESSORS, keep this in sync with what's actually deployed ═══════
   When a new sub-processor is added: append to this list AND email
   privacy@ subscribers (or post an in-app banner) 30 days before go-live.
*/
const SUBPROCESSORS = [
  {
    name: 'Amazon Web Services (AWS)',
    purpose: 'Hosting & infrastructure (compute, storage, networking)',
    data: 'All customer data (encrypted at rest)',
    location: 'eu-central-1 (Frankfurt) for EU/UK; ap-south-1 (Mumbai) for IN/APAC; us-east-1 (Virginia) for NA',
    safeguards: 'AWS DPA + SCCs; ISO 27001, SOC 1/2/3 attested',
  },
  {
    name: 'MongoDB Atlas',
    purpose: 'Managed database for pipeline data',
    data: 'Customer pipeline data (encrypted at rest, mirrored regions)',
    location: 'Same region as AWS hosting (eu-central-1 / ap-south-1 / us-east-1)',
    safeguards: 'MongoDB DPA + SCCs; SOC2 Type II, ISO 27001',
  },
  {
    name: 'Anthropic',
    purpose: 'LLM inference for AI Deal Chat (Claude Sonnet)',
    data: 'In-memory prompt context only, zero retention; no training on customer data',
    location: 'EU residency available on request via AWS Bedrock (eu-central-1)',
    safeguards: 'Anthropic Commercial Terms + DPA; no-training commitment',
  },
  {
    name: 'Slack Technologies',
    purpose: 'Internal lead-capture webhook (no customer pipeline data)',
    data: 'Lead-form email + page name only, never customer pipeline data',
    location: 'us-east-1 (Slack-managed)',
    safeguards: 'Slack DPA + SCCs; SOC2 Type II, ISO 27001',
  },
  {
    name: 'PostHog',
    purpose: 'Product analytics for the Forsyt application (pseudonymized)',
    data: 'Pseudonymized user telemetry, never customer pipeline data; opt-in via consent banner',
    location: 'EU (eu.posthog.com)',
    safeguards: 'PostHog DPA + SCCs; SOC2 Type II',
  },
  {
    name: 'Google (Tag Manager + Analytics)',
    purpose: 'Marketing-site analytics, never loaded inside the application',
    data: 'Anonymized site-visit events; loads only after explicit cookie consent',
    location: 'Google Cloud (multi-region)',
    safeguards: 'Google Ads Data Processing Terms + SCCs',
  },
];

export default function SubProcessors() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Sub-processors, Forsyt"
        description="The complete, current list of sub-processors Forsyt engages to deliver the Revenue OS service. Updated whenever vendors change, with 30-day advance notice."
        path="/sub-processors"
      />

      {/* Hero */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
            <Server className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">Sub-processors</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight" data-testid="subprocessors-headline">
            Every third party that{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              touches your data
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            We list every sub-processor we engage, what data they see, where they
            host, and the contractual safeguards we&rsquo;ve put in place. Customers receive
            <strong> 30 days&rsquo; advance notice</strong> of any change to this list, with the
            right to object.
          </p>
        </div>
      </section>

      {/* Notice strip */}
      <section className="py-6 bg-blue-50 border-y border-blue-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-3 text-gray-800">
              <Shield className="w-5 h-5 text-blue-600" />
              <p>
                <strong>Last updated:</strong> February 2026 ·{' '}
                <strong>Version:</strong> 2.0 ·{' '}
                <strong>Next review:</strong> May 2026
              </p>
            </div>
            <a
              href="mailto:privacy@forsyt.com?subject=Subscribe%20to%20sub-processor%20notifications"
              className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:underline"
              data-testid="sub-subscribe"
            >
              <Mail className="w-4 h-4" /> Subscribe to change notifications
            </a>
          </div>
        </div>
      </section>

      {/* Entity disclosure strip */}
      <section className="py-4 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs text-gray-500" data-testid="sub-entity-strip">
            <strong className="text-gray-700">Legal entity:</strong> Credos Forsyt Private Limited (operating as Forsyt) &middot;{' '}
            <strong className="text-gray-700">Registered office:</strong> New Delhi, India &middot;{' '}
            <strong className="text-gray-700">Privacy:</strong> privacy@forsyt.com
          </p>
        </div>
      </section>

      {/* Table */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-bold text-gray-500 uppercase tracking-widest border-b-2 border-gray-200">
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Purpose</th>
                  <th className="px-4 py-3">Data accessed</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Safeguards</th>
                </tr>
              </thead>
              <tbody>
                {SUBPROCESSORS.map((s, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 align-top" data-testid={`sub-row-${i}`}>
                    <td className="px-4 py-4 font-bold text-gray-900">{s.name}</td>
                    <td className="px-4 py-4 text-gray-700 leading-relaxed">{s.purpose}</td>
                    <td className="px-4 py-4 text-gray-600 text-xs leading-relaxed">{s.data}</td>
                    <td className="px-4 py-4 text-gray-600 text-xs leading-relaxed">{s.location}</td>
                    <td className="px-4 py-4 text-xs text-gray-700 leading-relaxed">{s.safeguards}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-4">
            {SUBPROCESSORS.map((s, i) => (
              <div key={i} className="rounded-xl border border-gray-200 p-5" data-testid={`sub-card-${i}`}>
                <h3 className="text-base font-bold text-gray-900 mb-2">{s.name}</h3>
                <div className="space-y-2 text-sm">
                  <p><strong className="text-gray-500 text-xs uppercase tracking-widest block mb-1">Purpose</strong>{s.purpose}</p>
                  <p><strong className="text-gray-500 text-xs uppercase tracking-widest block mb-1">Data accessed</strong>{s.data}</p>
                  <p><strong className="text-gray-500 text-xs uppercase tracking-widest block mb-1">Location</strong>{s.location}</p>
                  <p><strong className="text-gray-500 text-xs uppercase tracking-widest block mb-1">Safeguards</strong>{s.safeguards}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom, change notification + back to security */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Globe className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
            We tell you before this list changes
          </h2>
          <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">
            When we add or replace a sub-processor, you receive 30 calendar
            days&rsquo; advance notice, by email and in-app banner, with the right to
            object on data-protection grounds. If we cannot remediate the
            objection, you may terminate the affected portion of your contract
            without penalty.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/security"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-md hover:shadow-lg transition-all"
              data-testid="sub-back-security"
            >
              Back to /security
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:privacy@forsyt.com?subject=Sub-processor%20question"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border border-gray-300 hover:border-blue-600 bg-white rounded-full transition-all"
              data-testid="sub-email"
            >
              <Mail className="w-4 h-4 text-blue-600" /> Email privacy@forsyt.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
